import React, { useState } from 'react';
import { compose } from 'redux';
import { formatToSpacedUpperCase } from '@shared/utils/strings';
import Charts from '@shared/components/charts/Charts';
import { getSegmentedGraphOptions, getSeriesBasicOptions } from '@shared/components/charts/options/get-segmented-graph-options';
import { withMetricsReport } from '@domain/dashboard/metrics-report';
import { withSegments } from '@domain/segments/segments.hocs';
import { getMetricData } from '@domain/_screens/overview/segmented/graph/graph-data';
import { isDataLoading } from '@shared/axios';
import { VIEW_TYPE } from '@shared/constants/dashboard/campaign-table-constants';
import { maxElementsOnGraph } from '@domain/segments/segments.utils';
import { withTagGroupsSegmented } from '@domain/segments/tag-groups-segmented/tag-groups-segmented.hocs';
import { SegmentsGraphTooltip } from '@shared/components/tooltips/segmented-tooltip/segments-tooltip';
import { withDashboardFilters } from '@domain/dashboard/dashboard-filters';
import { withCampaigns } from '@domain/campaigns';
import { withHotSpots } from '@domain/dashboard/hotspot';
import { SegmentedGraphWrapper } from '../../dashboard/segmented/segmented-graph/segmented-graph.styles';
import SegmentedGraphSkeleton from '../../dashboard/segmented/segmented-graph/segmented-graph-skeleton';
import { SegmentedGraphProps } from './segments-graph.types';
import { segmentedGraphStyles } from './segments-graph.styles';
import { segmentsGraphColors } from './segments-graph.constants';

const SegmentedGraphImpl = ({
    campaigns,
    dashboardFilters,
    hotSpots,
    metricsReport,
    tagGroups,
    tagGroupsSegmented,
}: SegmentedGraphProps) => {
    const [isAccumulated, setIsAccumulated] = useState(false);
    const { firstMetric } = metricsReport;

    const { selectedGroup } = tagGroups;
    const segmentedTags = tagGroupsSegmented[selectedGroup.slug];

    const isCampaignsLoading = isDataLoading(campaigns.loadState);
    const isTagGroupsLoading = isDataLoading(tagGroups.loadState);
    const isLoading = isCampaignsLoading
        || isTagGroupsLoading
        || !segmentedTags
        || Object.values(segmentedTags).find(({ loadState }) => isDataLoading(loadState));

    const { segmentation } = dashboardFilters;

    if (selectedGroup.slug === VIEW_TYPE.standard) {
        return '';
    }

    if (isLoading) {
        return (
            <SegmentedGraphWrapper>
                <SegmentedGraphSkeleton />
            </SegmentedGraphWrapper>
        );
    }

    const compareValuesBySegmentation = (a, b) => (
        (a[segmentation.toLowerCase()] > b[segmentation.toLowerCase()]) ? 1 : -1
    );

    const sortedByTotalCostTags = Object.values(segmentedTags)
        .sort(
            ({ total: total1 }, { total: total2 }) => total2?.cost - total1?.cost,
        ).slice(0, maxElementsOnGraph);


    const series = sortedByTotalCostTags.map((
        { segmented, loadState, tag }, index,
    ) => {
        const total = segmented ? segmented.slice(0).sort(compareValuesBySegmentation) : [];
        return ({
            opacity: 0.7,
            color: segmentsGraphColors[index],
            name: tag ? formatToSpacedUpperCase(tag) : '',
            data: !isDataLoading(loadState) && getMetricData(total, firstMetric),
            yAxisIndex: 0,
            ...getSeriesBasicOptions(total),
        });
    });

    const [firstSlug] = Object.keys(segmentedTags);
    const firstSegmentation = segmentedTags[firstSlug];
    const total = firstSegmentation?.segmented?.sort(compareValuesBySegmentation);

    const tooltip = {
        formatter: SegmentsGraphTooltip(),
    };

    const toggleAccumulated = () => setIsAccumulated(prevState => !prevState);

    const options = getSegmentedGraphOptions({
        hotSpots: hotSpots.data,
        segmentation,
        total,
        tooltip,
        series,
        isAccumulated,
        toggleAccumulated,
        legend: {
            data: sortedByTotalCostTags.map(({ tag }) => formatToSpacedUpperCase(tag)),
            selectedMode: true,
        },
    });

    return (
        <SegmentedGraphWrapper>
            <Charts
                styles={segmentedGraphStyles}
                option={options}
            />
        </SegmentedGraphWrapper>
    );
};

SegmentedGraphImpl.defaultProps = {
    hotSpots: [],
};

export const SegmentedGraphByTagGroup = compose(
    withCampaigns,
    withDashboardFilters,
    withHotSpots,
    withMetricsReport,
    withSegments,
    withTagGroupsSegmented,
)(SegmentedGraphImpl);
